import React, { useEffect, useState } from "react";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { searchPlugin } from "@react-pdf-viewer/search";
import { toolbarPlugin } from "@react-pdf-viewer/toolbar";
import { zoomPlugin } from "@react-pdf-viewer/zoom";
import styled, { ThemeProvider, createGlobalStyle } from "styled-components";
import { useSelector } from "react-redux";
import { userSelector } from "../../user-redux/selector";
import { appSelector } from "../../redux/selector";
import Key from "../../../clientVariables.json";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { renderAsync } from "docx-preview";
import { useRef, useCallback } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SearchIcon from "@mui/icons-material/Search";
import { InputBase } from "@mui/material";


const customTheme = {
  viewer: {
    backgroundColor: "#fff",
  },
  toolbar: {
    backgroundColor: "#f5f5f5",
    color: "#333 !important",
  },
};

const RpvFix = createGlobalStyle`
  /* Let the popover escape the viewer’s internal wrappers */
  .rpv-default-layout__container,
  .rpv-default-layout__main,
  .rpv-default-layout__body,
  .rpv-default-layout__toolbar,
  .rpv-toolbar {
    overflow: visible !important;
  }

  /* Make the popover stack above MUI drawers/dialogs */
  .rpv-core__popover,
  .rpv-search__popover,
  .rpv-core__popover-body {
    z-index: 2000 !important;
  }

  /* Defensive: remove stacking-context creators */
  .rpv-default-layout__container,
  .rpv-default-layout__toolbar {
    transform: none !important;
    filter: none !important;
    perspective: none !important;
    contain: none !important;
  }
`;


// In your styled viewer (if needed)
const StyledViewer = styled(Viewer)`
   background-color: ${({ theme }) => theme.viewer.backgroundColor};
   height: 100%;

   /* Don’t clip the popover */
   .rpv-default-layout__container {
     overflow: visible !important;
   }

   /* Make sure popovers stack above surrounding UI (MUI drawers/modals ~1200) */
   .rpv-core__popover,
   .rpv-search__popover {
     z-index: 1300 !important;
   }
 `;


const DocxViewerContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  overflow: hidden;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
    flex: 1;
  }
`;

const DocxToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  background-color: #ffffff;
  color: #333333;
  z-index: 1000;
  min-height: 48px;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`;

const DocxContent = styled.div`
  flex: 1;
  overflow: auto;
  padding: 20px;
  background-color: #ffffff;
  
  mark.docx-search-highlight {
    background-color: #ffff00;
    color: #000000;
    padding: 0 1px;
    border-radius: 2px;
  }

  /* Current match: same yellow, darker ring so it stands out */
  mark.docx-search-highlight.docx-search-active {
    background-color: #ffff00;
    outline: 2px solid #f9a825;
    box-shadow: 0 0 0 1px #f57f17;
  }

  .docx-wrapper {
    background-color: #ffffff !important;
    padding: 0 !important;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  article.docx {
    background-color: #ffffff !important;
    box-shadow: 0 0 10px rgba(0,0,0,0.1) !important;
    margin-bottom: 30px !important;
    padding: 50px !important;
    width: 850px !important;
    min-height: 1000px !important;
    color: #000000 !important;
  }
`;
const ResumePdfViewer = () => {
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    sidebarTabs: (defaultTabs) => [],
  });
  const searchPluginInstance = searchPlugin();
  const toolbarPluginInstance = toolbarPlugin();
  const zoomPluginInstance = zoomPlugin();
  const [defaultFile, setDefaultFile] = useState();
  const [fileType, setFileType] = useState("");
  const { candidateSelectedResume, selectedCandidate, candidateDetails } =
    useSelector(appSelector);
  const [zoom, setZoom] = useState(1.0);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchMatchIndex, setSearchMatchIndex] = useState(0);
  const [searchMatchCount, setSearchMatchCount] = useState(0);
  const docxRef = useRef(null);
  const docxSearchMarksRef = useRef([]);
  const lastDocxSearchTermRef = useRef("");
  const searchMatchIndexRef = useRef(0);

  useEffect(() => {
    //console.log("Admin ResumePdfViewer - Selected Resume:", candidateSelectedResume);
    if (candidateSelectedResume?.filename) {
      // Guard against a stale resume: when the selected candidate changes, the new
      // candidate_id can update before candidateSelectedResume does. Building a URL from
      // the new candidate_id + old filename triggers a 404 for a file that doesn't exist.
      const resumeCandidateId = candidateSelectedResume?.candidate_id;
      const activeCandidateId = selectedCandidate?.candidate_id;
      if (
        resumeCandidateId != null &&
        activeCandidateId != null &&
        String(resumeCandidateId) !== String(activeCandidateId)
      ) {
        return;
      }

      setSearchTerm("");
      setSearchMatchCount(0);
      setSearchMatchIndex(0);
      docxSearchMarksRef.current = [];
      lastDocxSearchTermRef.current = "";
      const fileUrl = parseInt(candidateSelectedResume?.resume_id) < 594500
        ? `https://${Key.aws_bucket_jnp}.s3.amazonaws.com/jnp_data/resume_${candidateSelectedResume?.resume_id}/${candidateSelectedResume?.filename}`
        : `${Key.domain}/candidate/get-resume/root/data/bench_data/attachments/cand_${selectedCandidate?.candidate_id}/${candidateSelectedResume?.filename}`;

      setDefaultFile(fileUrl);
      //console.log("Admin ResumePdfViewer - Constructed File URL:", fileUrl);

      // Determine file type based on extension
      const extension = candidateSelectedResume.filename.split('.').pop().toLowerCase();
      setFileType(extension);
      //console.log("Admin ResumePdfViewer - Detected File Type:", extension);
    }
  }, [candidateSelectedResume?.resume_id, candidateSelectedResume?.filename, candidateDetails, selectedCandidate?.candidate_id]);

  const applyZoom = () => {
    if (docxRef.current) {
      const wrapper = docxRef.current.querySelector('.docx-wrapper');
      if (wrapper) {
        wrapper.style.zoom = zoom;
      } else {
        docxRef.current.style.zoom = zoom;
      }
    }
  };

  useEffect(() => {
    applyZoom();
  }, [zoom]);

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = defaultFile;
    link.download = candidateSelectedResume?.filename || "document.docx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.1, 2.0));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.1, 0.5));

  const clearDocxSearchHighlights = useCallback(() => {
    if (!docxRef.current) return;
    docxRef.current.querySelectorAll("mark.docx-search-highlight").forEach((mark) => {
      const parent = mark.parentNode;
      if (!parent) return;
      parent.replaceChild(document.createTextNode(mark.textContent), mark);
      parent.normalize();
    });
    docxSearchMarksRef.current = [];
    searchMatchIndexRef.current = 0;
    setSearchMatchCount(0);
    setSearchMatchIndex(0);
  }, []);

  const highlightAllDocxMatches = useCallback((term) => {
    clearDocxSearchHighlights();
    if (!term || !docxRef.current) return 0;

    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const root = docxRef.current;
    const textNodes = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        if (node.parentElement?.closest("mark.docx-search-highlight")) {
          return NodeFilter.FILTER_REJECT;
        }
        if (!node.textContent?.length) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    let node = walker.nextNode();
    while (node) {
      textNodes.push(node);
      node = walker.nextNode();
    }

    let matchCount = 0;
    textNodes.forEach((textNode) => {
      const text = textNode.textContent;
      const regex = new RegExp(escaped, "gi");
      if (!regex.test(text)) return;

      const fragment = document.createDocumentFragment();
      let lastIndex = 0;
      let match;
      const splitRegex = new RegExp(escaped, "gi");
      while ((match = splitRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
        }
        const mark = document.createElement("mark");
        mark.className = "docx-search-highlight";
        mark.textContent = match[0];
        fragment.appendChild(mark);
        docxSearchMarksRef.current.push(mark);
        matchCount += 1;
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < text.length) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
      }
      textNode.parentNode?.replaceChild(fragment, textNode);
    });

    setSearchMatchCount(matchCount);
    return matchCount;
  }, [clearDocxSearchHighlights]);

  const goToDocxMatch = useCallback((index) => {
    const marks = docxSearchMarksRef.current;
    if (!marks.length) return;

    const safeIndex = ((index % marks.length) + marks.length) % marks.length;
    marks.forEach((mark) => mark.classList.remove("docx-search-active"));
    const activeMark = marks[safeIndex];
    activeMark.classList.add("docx-search-active");
    searchMatchIndexRef.current = safeIndex;
    setSearchMatchIndex(safeIndex);
    activeMark.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const runDocxSearch = useCallback(
    ({ forward = true, restart = false } = {}) => {
      const term = searchTerm.trim();
      if (!term) {
        clearDocxSearchHighlights();
        lastDocxSearchTermRef.current = "";
        return;
      }

      const termChanged = lastDocxSearchTermRef.current !== term;
      if (restart || termChanged || docxSearchMarksRef.current.length === 0) {
        const count = highlightAllDocxMatches(term);
        lastDocxSearchTermRef.current = term;
        if (count > 0) {
          goToDocxMatch(0);
        }
        return;
      }

      const nextIndex = forward
        ? searchMatchIndexRef.current + 1
        : searchMatchIndexRef.current - 1;
      goToDocxMatch(nextIndex);
    },
    [searchTerm, clearDocxSearchHighlights, highlightAllDocxMatches, goToDocxMatch]
  );

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      runDocxSearch({ forward: true });
    }
  };

  const executeSearch = () => {
    runDocxSearch({ forward: true });
  };

  useEffect(() => {
    if (fileType === "docx" && defaultFile && docxRef.current) {
      //console.log("Admin ResumePdfViewer - Starting DOCX render for:", defaultFile);
      fetch(defaultFile)
        .then((response) => {
          //console.log("Admin ResumePdfViewer - Fetch response status:", response.status);
          if (!response.ok) throw new Error(`Fetch failed with status ${response.status}`);
          return response.arrayBuffer();
        })
        .then((buffer) => {
          //console.log("Admin ResumePdfViewer - File buffer loaded, length:", buffer.byteLength);
          docxRef.current.innerHTML = "";
          return renderAsync(buffer, docxRef.current, null, {
            className: "docx",
            inWrapper: true,
            ignoreLastRenderedPageBreak: false,
          });
        })
        .then(() => {
          //console.log("Admin ResumePdfViewer - DOCX render completed successfully");
          applyZoom();
          if (lastDocxSearchTermRef.current) {
            const count = highlightAllDocxMatches(lastDocxSearchTermRef.current);
            if (count > 0) goToDocxMatch(0);
          }
        })
        .catch((error) => {
          console.error("Admin ResumePdfViewer - Error in DOCX rendering pipeline:", error);
        });
    } else {
      console.log("Admin ResumePdfViewer - Render criteria not met:", {
        fileType,
        hasFile: !!defaultFile,
        hasRef: !!docxRef.current,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- re-render docx only when file changes
  }, [fileType, defaultFile]);

  const renderViewer = () => {
    if (!defaultFile) return <div>Loading...</div>;

    if (fileType === "pdf") {
      return (
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
          <StyledViewer
            fileUrl={defaultFile}
            plugins={[
              defaultLayoutPluginInstance,
              searchPluginInstance,
              toolbarPluginInstance,
              zoomPluginInstance,
            ]}
          />
        </Worker>
      );
    } else if (fileType === 'docx') {
      return (
        <DocxViewerContainer>
          <DocxToolbar>
            {/* Left: Search */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                bgcolor: '#f1f3f4', 
                borderRadius: 1, 
                px: 1,
                width: 200
              }}>
                <SearchIcon fontSize="small" sx={{ color: "#5f6368", mr: 1 }} />
                <InputBase
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchTerm(value);
                    if (!value.trim()) {
                      clearDocxSearchHighlights();
                      lastDocxSearchTermRef.current = "";
                    }
                  }}
                  onKeyDown={handleSearch}
                  sx={{ color: '#3c4043', fontSize: '0.875rem', width: '100%' }}
                />
                {searchMatchCount > 0 && (
                  <Typography variant="caption" sx={{ color: "#5f6368", mx: 0.5, whiteSpace: "nowrap" }}>
                    {searchMatchIndex + 1}/{searchMatchCount}
                  </Typography>
                )}
                <IconButton onClick={executeSearch} size="small" sx={{ color: "#5f6368" }} title="Next match (Enter)">
                  <SearchIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Middle: Zoom */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1
              }}>
                <Tooltip title="Zoom Out">
                  <IconButton onClick={handleZoomOut} size="small" sx={{ color: "#5f6368", border: '1px solid #dadce0' }}>
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Typography variant="body2" sx={{ color: "#3c4043", minWidth: 50, textAlign: 'center', fontWeight: 500 }}>
                  {Math.round(zoom * 100)}%
                </Typography>
                <Tooltip title="Zoom In">
                  <IconButton onClick={handleZoomIn} size="small" sx={{ color: "#5f6368", border: '1px solid #dadce0' }}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Right: Download */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title="Download">
                <IconButton onClick={handleDownload} size="small" sx={{ 
                  color: "#5f6368",
                  '&:hover': { bgcolor: '#f1f3f4' }
                }}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </DocxToolbar>
          <DocxContent ref={docxRef} />
        </DocxViewerContainer>
      );
    } else if (fileType === "doc") {
      // Fallback for legacy .doc files with consistent toolbar (Search hidden as not supported)
      return (
        <DocxViewerContainer>
          <DocxToolbar>
            {/* Left: Empty (Search not supported for .doc) */}
            <Box sx={{ flex: 1 }} />

            {/* Middle: Zoom (CSS Scale for .doc iframe) */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1
              }}>
                <Tooltip title="Zoom Out">
                  <IconButton onClick={handleZoomOut} size="small" sx={{ color: "#5f6368", border: '1px solid #dadce0' }}>
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Typography variant="body2" sx={{ color: "#3c4043", minWidth: 50, textAlign: 'center', fontWeight: 500 }}>
                  {Math.round(zoom * 100)}%
                </Typography>
                <Tooltip title="Zoom In">
                  <IconButton onClick={handleZoomIn} size="small" sx={{ color: "#5f6368", border: '1px solid #dadce0' }}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Right: Download */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title="Download">
                <IconButton onClick={handleDownload} size="small" sx={{ 
                  color: "#5f6368",
                  '&:hover': { bgcolor: '#f1f3f4' }
                }}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </DocxToolbar>
          <Box sx={{ 
            flex: 1, 
            width: '100%', 
            overflow: 'auto', 
            display: 'flex', 
            justifyContent: 'center',
            bgcolor: '#f0f2f5',
            p: 2
          }}>
            <Box sx={{
              width: '850px',
              height: 'fit-content',
              minHeight: '100%',
              transform: `scale(${zoom})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease-in-out',
              boxShadow: '0 0 10px rgba(0,0,0,0.1)'
            }}>
              <iframe
                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(defaultFile)}`}
                title="Document Viewer"
                style={{ width: '100%', height: '1000px', border: 'none' }}
              />
            </Box>
          </Box>
        </DocxViewerContainer>
      );
    } else {
      return (
        <div>
          Unsupported file type. <a href={defaultFile} target="_blank" rel="noopener noreferrer">Download file</a>
        </div>
      );
    }
  };

  return (
    <ThemeProvider theme={customTheme}>
      <RpvFix />
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}>
        {renderViewer()}
      </Box>
    </ThemeProvider>
  );
};

export default ResumePdfViewer;